import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Camera, FileImage, X, RefreshCw, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { asBalanceResult } from "@/lib/rpcResults";
import { convertPdfToImages, isPdfFile, getPdfPageCount } from "@/lib/pdfUtils";
import * as Sentry from "@sentry/react";
import {
  isInsufficientCreditsError,
  toEdgeFunctionError,
  type EdgeFunctionError,
} from "@/lib/edgeFunctionErrors";


interface UploadAreaProps {
  onUpload: (file: File) => void;
}

interface AnalysisResult {
  storeName: string;
  date: string;
  total: number;
  items: unknown[];
  storeAddress?: string;
}

export const UploadArea = ({ onUpload }: UploadAreaProps) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState<string[]>([]);
  const [hasBalance, setHasBalance] = useState<boolean | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Helper function to handle insufficient balance (DRY principle)
  const handleInsufficientBalance = (action: string) => {
    const sentryEventId = Sentry.captureMessage(`User attempted upload with insufficient balance`, {
      level: 'warning',
      tags: {
        component: 'UploadArea',
        action,
        error_code: '2001',
      },
    });
    const supportId = sentryEventId
      ? sentryEventId.substring(0, 8).toUpperCase()
      : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;
    toast({
      title: t('uploadArea.insufficientBalance'),
      description: t('uploadArea.insufficientBalanceWithSupport', { supportId }),
      variant: "destructive",
      duration: Infinity,
    });
  };

  // Pre-fetch balance on mount to enable synchronous checks (required for mobile Safari)
  useEffect(() => {
    const fetchBalance = async () => {
      setIsCheckingBalance(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoggedIn(false);
          setHasBalance(null); // Don't set false - that's for insufficient balance
          return;
        }

        setIsLoggedIn(true);

        const { data: balanceData, error: balanceError } = await supabase.rpc('check_balance', {
          p_user_id: user.id
        });

        if (balanceError) {
          console.error('Error pre-fetching balance:', balanceError);
          setHasBalance(true); // Allow upload if check fails, will be caught later
          return;
        }

        const currentBalance = asBalanceResult(balanceData)?.balance ?? 0;
        setHasBalance(currentBalance > 0);
      } catch (error) {
        console.error('Error in balance pre-fetch:', error);
        setHasBalance(true); // Allow upload if check fails
      } finally {
        setIsCheckingBalance(false);
      }
    };

    fetchBalance();

    // Listen for balance updates (e.g., after credit consumption)
    const handleCreditsUpdated = () => fetchBalance();
    window.addEventListener('creditsUpdated', handleCreditsUpdated);
    return () => window.removeEventListener('creditsUpdated', handleCreditsUpdated);
  }, []);

  const checkBalanceBeforeUpload = async (action: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: t('uploadArea.authRequired'),
          description: t('uploadArea.pleaseLogin'),
          variant: "destructive",
        });
        return false;
      }

      const { data: balanceData, error: balanceError } = await supabase.rpc('check_balance', {
        p_user_id: user.id
      });

      if (balanceError) {
        console.error('Error checking balance:', balanceError);
        return true; // Allow upload if check fails, will be caught later
      }

      const currentBalance = asBalanceResult(balanceData)?.balance ?? 0;

      if (currentBalance <= 0) {
        console.warn(`[UploadArea] User attempted ${action} with zero balance`);

        const sentryEventId = Sentry.captureMessage(`User attempted upload with insufficient balance`, {
          level: 'warning',
          tags: {
            component: 'UploadArea',
            action: action,
            error_code: '2001', // INSUFFICIENT_CREDITS
          },
          extra: {
            userId: user.id,
            userEmail: user.email,
            currentBalance: currentBalance,
          },
        });

        const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

        toast({
          title: t('uploadArea.insufficientBalance'),
          description: t('uploadArea.insufficientBalanceWithSupport', { supportId }),
          variant: "destructive",
          duration: Infinity, // Keep open until user closes
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in balance pre-check:', error);
      return true; // Allow upload if check fails, will be caught later
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const canUpload = await checkBalanceBeforeUpload('drag_drop');
      if (canUpload) {
        handleFile(files[0]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = async (file: File) => {
    const isPdfFile_ = isPdfFile(file);

    // Validate file type
    if (!file.type.startsWith('image/') && !isPdfFile_) {
      toast({
        title: t('uploadArea.invalidFileType'),
        description: t('uploadArea.invalidFileTypeDescription'),
        variant: "destructive",
      });
      return;
    }

    // Validate file size (20MB for PDFs, 10MB for images)
    const maxSize = isPdfFile_ ? 20 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: t('uploadArea.fileTooLarge'),
        description: t('uploadArea.fileTooLargeDescription', { maxSize: isPdfFile_ ? '20MB' : '10MB' }),
        variant: "destructive",
      });
      return;
    }

    setIsPdf(isPdfFile_);

    if (isPdfFile_) {
      await handlePdfFile(file);
    } else {
      await handleImageFile(file);
    }
  };

  const handlePdfFile = async (file: File) => {
    console.log('[UploadArea.handlePdfFile] Starting PDF processing for:', file.name);

    try {
      console.log('[UploadArea.handlePdfFile] Showing processing toast...');
      toast({
        title: t('uploadArea.processingPdf'),
        description: t('uploadArea.convertingPdf'),
      });

      // Get page count
      console.log('[UploadArea.handlePdfFile] Getting page count...');
      const pageCount = await getPdfPageCount(file);
      console.log('[UploadArea.handlePdfFile] Page count received:', pageCount);
      setPdfPageCount(pageCount);

      // Convert PDF to images
      console.log('[UploadArea.handlePdfFile] Converting PDF to images...');
      const images = await convertPdfToImages(file);
      const imageDataUrls = images.map(img => img.imageDataUrl);
      console.log('[UploadArea.handlePdfFile] Images converted. Count:', imageDataUrls.length);
      setPdfPages(imageDataUrls);

      // Show first page preview
      console.log('[UploadArea.handlePdfFile] Setting first page as preview...');
      setUploadedImage(imageDataUrls[0]);
      setCurrentPdfPage(1);

      console.log('[UploadArea.handlePdfFile] Showing PDF loaded toast...');
      toast({
        title: t('uploadArea.pdfLoaded'),
        description: t('uploadArea.pagesReady', { count: pageCount }),
      });

      // Analyze all pages
      console.log('[UploadArea.handlePdfFile] Starting analysis of PDF pages...');
      await analyzePdfPages(imageDataUrls, file);
      console.log('[UploadArea.handlePdfFile] PDF processing completed successfully');

    } catch (error) {
      console.error('[UploadArea.handlePdfFile] Error processing PDF:', error);
      console.error('[UploadArea.handlePdfFile] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack',
      });
      toast({
        title: t('uploadArea.pdfProcessingFailed'),
        description: error instanceof Error ? error.message : t('uploadArea.couldNotProcessPdf'),
        variant: "destructive",
      });
      setIsAnalyzing(false);
      clearImage();
    }
  };

  const analyzePdfPages = async (imageDataUrls: string[], file: File) => {
    console.log('[UploadArea.analyzePdfPages] Starting analysis. Pages:', imageDataUrls.length);
    setIsAnalyzing(true);
    let session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'] = null;

    try {
      console.log('[UploadArea.analyzePdfPages] Checking Supabase...');
      if (!supabase) {
        throw new Error(t('uploadArea.cloudNotEnabled'));
      }

      console.log('[UploadArea.analyzePdfPages] Getting session...');
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      session = sessionData.session;

      if (sessionError) {
        console.error('[UploadArea.analyzePdfPages] Session error:', sessionError);
        throw new Error(t('uploadArea.mustBeLoggedIn'));
      }

      if (!session) {
        console.error('[UploadArea.analyzePdfPages] No session found');
        throw new Error(t('uploadArea.mustBeLoggedIn'));
      }

      console.log('[UploadArea.analyzePdfPages] Session found. User:', session.user.email);

      // Check balance before processing
      console.log('[UploadArea.analyzePdfPages] Checking balance...');
      const { data: balanceData, error: balanceError } = await supabase.rpc('check_balance', {
        p_user_id: session.user.id
      });

      if (balanceError) {
        console.error('Error checking balance:', balanceError);
        throw new Error('Unable to check credit balance');
      }

      const currentBalance = asBalanceResult(balanceData)?.balance ?? 0;
      console.log('[UploadArea.analyzePdfPages] Current balance:', currentBalance);

      if (currentBalance <= 0) {
        console.warn('[UploadArea.analyzePdfPages] Insufficient balance');
        toast({
          title: 'Insufficient Credits',
          description: 'You do not have enough credits to analyze this receipt. Please add more credits.',
          variant: "destructive",
        });
        setIsAnalyzing(false);
        clearImage();
        return;
      }

      // For multi-page PDFs, analyze each page and combine results
      // For now, we'll analyze the first page (most PDFs have receipt on first page)
      const imageBase64 = imageDataUrls[0];
      console.log('[UploadArea.analyzePdfPages] Image Base64 length:', imageBase64.length);

      console.log('[UploadArea.analyzePdfPages] Invoking analyze-receipt function...');
      const { data, error } = await supabase.functions.invoke('analyze-receipt', {
        body: {
          imageBase64,
          isPdf: true,
          pageCount: imageDataUrls.length
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('[UploadArea.analyzePdfPages] Function error:', error);
        // invoke() resolves with a FunctionsHttpError whose .context is the
        // raw Response, so the body has to be parsed — reading .context.code
        // directly always yielded undefined.
        throw await toEdgeFunctionError(error);
      }

      console.log('[UploadArea.analyzePdfPages] Function response received:', data);

      // Check if we got an error response (but no error object)
      if (data?.error) {
        const enhancedError = new Error(data.error) as EdgeFunctionError;
        enhancedError.sentryEventId = data.sentryEventId;
        enhancedError.supportInfo = data.supportInfo;
        enhancedError.code = data.code;
        throw enhancedError;
      }

      if (!data?.receipt) {
        console.error('[UploadArea.analyzePdfPages] No receipt data in response');
        throw new Error(t('uploadArea.noReceiptData'));
      }

      console.log('[UploadArea.analyzePdfPages] Setting analysis result:', data.receipt);
      setAnalysisResult(data.receipt);

      // The credit is debited server-side by the analyze-receipt function, so
      // it can no longer be skipped by calling the endpoint directly. We only
      // refresh the balance display here.
      console.log('[UploadArea.analyzePdfPages] Credit debited server-side. Balance:', data.creditBalance);
      window.dispatchEvent(new Event('creditsUpdated'));

      console.log('[UploadArea.analyzePdfPages] Showing success toast...');
      toast({
        title: t('uploadArea.analysisComplete'),
        description: t('uploadArea.foundItems', { count: data.receipt.items.length, storeName: data.receipt.storeName }),
      });

      console.log('[UploadArea.analyzePdfPages] Calling onUpload callback...');
      onUpload(file);
      console.log('[UploadArea.analyzePdfPages] Analysis completed successfully');
    } catch (error) {
      console.error('[UploadArea.analyzePdfPages] Error analyzing receipt:', error);

      // The server-side credit gate rejected this scan. That is an expected
      // outcome, not a failure — surface it as an insufficient-balance
      // message rather than a generic "analysis failed", and refresh the
      // balance display so the UI stops offering uploads.
      if (await isInsufficientCreditsError(error)) {
        window.dispatchEvent(new Event('creditsUpdated'));
        handleInsufficientBalance('analyze_pdf');
        return;
      }

      // Errors from the function arrive here already normalised by
      // toEdgeFunctionError, carrying the server's message and code.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      // Map edge-function codes to our support error codes.
      const errorCode =
        (error as EdgeFunctionError).code === 'RECEIPT_VALIDATION_FAILED'
          ? '1004' // RECEIPT_VALIDATION_FAILED
          : '1001'; // Default: RECEIPT_PROCESSING_FAILED

      console.error('[UploadArea.analyzePdfPages] Error details:', {
        message: errorMessage,
        stack: error instanceof Error ? error.stack : 'No stack',
      });

      // Log to Sentry with full technical details for admin investigation
      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'UploadArea',
          action: 'analyze_pdf',
          error_code: errorCode,
        },
        extra: {
          userId: session?.user?.id,
          userEmail: session?.user?.email,
          pageCount: imageDataUrls.length,
          errorMessage: errorMessage,
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      // Show vague user-friendly message (no technical details)
      toast({
        title: t('uploadArea.analysisFailed'),
        description: t('uploadArea.analysisFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity, // Keep open until user closes
      });
    } finally {
      console.log('[UploadArea.analyzePdfPages] Setting isAnalyzing to false');
      setIsAnalyzing(false);
    }
  };

  const handleImageFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageBase64 = e.target?.result as string;
      setUploadedImage(imageBase64);

      toast({
        title: t('uploadArea.receiptUploaded'),
        description: t('uploadArea.analyzingReceipt'),
      });

      setIsAnalyzing(true);
      let session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'] = null;

      try {
        if (!supabase) {
          throw new Error(t('uploadArea.cloudNotEnabled'));
        }

        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        session = sessionData.session;

        if (sessionError || !session) {
          throw new Error(t('uploadArea.mustBeLoggedIn'));
        }

        // Check balance before processing
        console.log('[UploadArea.handleImageFile] Checking balance...');
        const { data: balanceData, error: balanceError } = await supabase.rpc('check_balance', {
          p_user_id: session.user.id
        });

        if (balanceError) {
          console.error('Error checking balance:', balanceError);
          throw new Error('Unable to check credit balance');
        }

        const currentBalance = asBalanceResult(balanceData)?.balance ?? 0;
        console.log('[UploadArea.handleImageFile] Current balance:', currentBalance);

        if (currentBalance <= 0) {
          console.warn('[UploadArea.handleImageFile] Insufficient balance');
          toast({
            title: 'Insufficient Credits',
            description: 'You do not have enough credits to analyze this receipt. Please add more credits.',
            variant: "destructive",
          });
          setIsAnalyzing(false);
          clearImage();
          return;
        }

        const { data, error } = await supabase.functions.invoke('analyze-receipt', {
          body: { imageBase64 },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (error) {
          console.error('Function error:', error);
          // See analyzePdfPages: the body must be parsed out of the Response.
          throw await toEdgeFunctionError(error);
        }

        // Check if we got an error response (but no error object)
        if (data?.error) {
          const enhancedError = new Error(data.error) as EdgeFunctionError;
          enhancedError.sentryEventId = data.sentryEventId;
          enhancedError.supportInfo = data.supportInfo;
          enhancedError.code = data.code;
          throw enhancedError;
        }

        if (!data?.receipt) {
          throw new Error(t('uploadArea.noReceiptData'));
        }

        setAnalysisResult(data.receipt);

        // Debited server-side by analyze-receipt; just refresh the display.
        console.log('[UploadArea.handleImageFile] Credit debited server-side. Balance:', data.creditBalance);
        window.dispatchEvent(new Event('creditsUpdated'));

        toast({
          title: t('uploadArea.analysisComplete'),
          description: t('uploadArea.foundItems', { count: data.receipt.items.length, storeName: data.receipt.storeName }),
        });

        onUpload(file);
      } catch (error) {
        console.error('Error analyzing receipt:', error);

        // The server-side credit gate rejected this scan. That is an expected
        // outcome, not a failure — surface it as an insufficient-balance
        // message rather than a generic "analysis failed", and refresh the
        // balance display so the UI stops offering uploads.
        if (await isInsufficientCreditsError(error)) {
          window.dispatchEvent(new Event('creditsUpdated'));
          handleInsufficientBalance('analyze_image');
          return;
        }

        // Errors from the function arrive here already normalised by
        // toEdgeFunctionError, carrying the server's message and code.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        // Map edge-function codes to our support error codes.
        const errorCode =
          (error as EdgeFunctionError).code === 'RECEIPT_VALIDATION_FAILED'
            ? '1004' // RECEIPT_VALIDATION_FAILED
            : '1001'; // Default: RECEIPT_PROCESSING_FAILED

        // Log to Sentry with full technical details for admin investigation
        const sentryEventId = Sentry.captureException(error, {
          level: 'error',
          tags: {
            component: 'UploadArea',
            action: 'analyze_image',
            error_code: errorCode,
          },
          extra: {
            userId: session?.user?.id,
            userEmail: session?.user?.email,
            fileType: file.type,
            fileSize: file.size,
            errorMessage: errorMessage,
          },
        });

        // Generate support ID - use Sentry ID if available, otherwise create a timestamp-based one
        const supportId = sentryEventId
          ? sentryEventId.substring(0, 8).toUpperCase()
          : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

        // Show vague user-friendly message (no technical details)
        toast({
          title: t('uploadArea.analysisFailed'),
          description: t('uploadArea.analysisFailedWithSupport', { supportId }),
          variant: "destructive",
          duration: Infinity, // Keep open until user closes
        });
      } finally {
        setIsAnalyzing(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setUploadedImage(null);
    setAnalysisResult(null);
    setIsPdf(false);
    setPdfPageCount(0);
    setCurrentPdfPage(1);
    setPdfPages([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const changeImage = () => {
    clearImage();
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-custom-lg border-0">
        <CardContent className="p-8">
          {uploadedImage ? (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={uploadedImage}
                  alt={isPdf ? `PDF page ${currentPdfPage}` : "Uploaded receipt"}
                  className="w-full max-w-md mx-auto rounded-lg shadow-custom-md"
                />
                {isPdf && pdfPageCount > 1 && (
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2" data-testid="pdf-page-counter">
                    <FileText className="h-4 w-4" />
                    {t('uploadArea.pageOf', { current: currentPdfPage, total: pdfPageCount })}
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={changeImage}
                    disabled={isAnalyzing}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={clearImage}
                    disabled={isAnalyzing}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {isPdf && pdfPageCount > 1 && (
                <div className="flex gap-2 justify-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newPage = Math.max(1, currentPdfPage - 1);
                      setCurrentPdfPage(newPage);
                      setUploadedImage(pdfPages[newPage - 1]);
                    }}
                    disabled={currentPdfPage === 1 || isAnalyzing}
                    data-testid="pdf-prev-page"
                  >
                    {t('uploadArea.previousPage')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newPage = Math.min(pdfPageCount, currentPdfPage + 1);
                      setCurrentPdfPage(newPage);
                      setUploadedImage(pdfPages[newPage - 1]);
                    }}
                    disabled={currentPdfPage === pdfPageCount || isAnalyzing}
                    data-testid="pdf-next-page"
                  >
                    {t('uploadArea.nextPage')}
                  </Button>
                </div>
              )}
              
              {isAnalyzing ? (
                <div className="text-center space-y-4">
                  <div className="bg-accent/50 rounded-lg p-6">
                    <div className="flex items-center justify-center mb-4">
                      <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{t('uploadArea.analyzingReceiptTitle')}</h3>
                    <p className="text-muted-foreground">
                      {t('uploadArea.analyzingDescription')}
                    </p>
                  </div>
                </div>
              ) : analysisResult ? (
                <div className="text-center space-y-4" data-testid="analysis-complete">
                  <div className="bg-accent/50 rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{t('uploadArea.analysisCompleteTitle')}</h3>
                    <p className="text-muted-foreground mb-4">
                      {analysisResult.storeName} • {new Date(analysisResult.date).toLocaleDateString()}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-card rounded-md p-3">
                        <div className="font-medium">{t('uploadArea.itemsFound')}</div>
                        <div className="text-2xl font-bold text-primary">{analysisResult.items.length}</div>
                      </div>
                      <div className="bg-card rounded-md p-3">
                        <div className="font-medium">{t('uploadArea.totalAmount')}</div>
                        <div className="text-2xl font-bold text-primary">${analysisResult.total}</div>
                      </div>
                    </div>
                    {analysisResult.storeAddress && (
                      <p className="text-xs text-muted-foreground mt-4">
                        {analysisResult.storeAddress}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2 justify-center">
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={changeImage}
                    >
                      {t('buttons.uploadAnother')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-12 text-center transition-all duration-300",
                isDragOver
                  ? "border-primary bg-accent/50 shadow-glow"
                  : "border-muted-foreground/30 hover:border-primary/50"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="upload-area"
            >
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="p-4 rounded-full shadow-glow">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">{t('uploadArea.uploadTitle')}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('uploadArea.dragAndDrop')}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      size="lg"
                      className="transition-all duration-300"
                      disabled={isCheckingBalance}
                      onClick={() => {
                        // Synchronous checks - required for mobile Safari user gesture chain
                        // Check login first (more specific error message)
                        if (isLoggedIn === false) {
                          toast({
                            title: t('uploadArea.authRequired'),
                            description: t('uploadArea.pleaseLogin'),
                            variant: "destructive",
                          });
                          return;
                        }
                        // Check balance (only if logged in)
                        if (hasBalance === false) {
                          handleInsufficientBalance('choose_file');
                          return;
                        }
                        fileInputRef.current?.click();
                      }}
                    >
                      {isCheckingBalance ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <FileImage className="mr-2 h-4 w-4" />
                      )}
                      {t('buttons.chooseFile')}
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      disabled={isCheckingBalance}
                      onClick={() => {
                        // Synchronous checks - required for mobile Safari user gesture chain
                        // Check login first (more specific error message)
                        if (isLoggedIn === false) {
                          toast({
                            title: t('uploadArea.authRequired'),
                            description: t('uploadArea.pleaseLogin'),
                            variant: "destructive",
                          });
                          return;
                        }
                        // Check balance (only if logged in)
                        if (hasBalance === false) {
                          handleInsufficientBalance('take_photo');
                          return;
                        }
                        cameraInputRef.current?.click();
                      }}
                    >
                      {isCheckingBalance ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Camera className="mr-2 h-4 w-4" />
                      )}
                      {t('buttons.takePhoto')}
                    </Button>
                  </div>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="camera-input"
                  />
                </div>

                <div className="text-xs text-muted-foreground">
                  {t('uploadArea.supportedFormats')}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!uploadedImage && (
        <Card className="shadow-custom-md border-0">
          <CardContent className="p-6">
            <h4 className="font-semibold mb-3">{t('uploadArea.tipsTitle')}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start">
                <div className="w-2 h-2 rounded-full bg-primary mt-1.5 mr-3 flex-shrink-0" />
                {t('uploadArea.tip1')}
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 rounded-full bg-primary mt-1.5 mr-3 flex-shrink-0" />
                {t('uploadArea.tip2')}
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 rounded-full bg-primary mt-1.5 mr-3 flex-shrink-0" />
                {t('uploadArea.tip3')}
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};